import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { apiService } from '../../../components/Config/api.service';
import SecondarySidebar from '../../../components/SideBar/SecondarySidebar';
import { FaUser, FaSortAlphaDown, FaSortAlphaUp, FaFilter } from 'react-icons/fa';
import Select from 'react-select';

const buildMonthOverviewParams = ({ offset, limit, month, sortOrder, searchQuery, selectedCategories, selectedDepartment }) => {
    const params = [
        `offset=${offset}`,
        `limit=${limit}`,
        `month=${month}`,
        `sort_by=name`,
        `sort_dir=${sortOrder}`
    ];
    if (searchQuery) params.push(`search=${encodeURIComponent(searchQuery)}`);
    if (selectedCategories.length > 0) params.push(`categories=${selectedCategories.join(',')}`);
    if (selectedDepartment) params.push(`department_id=${selectedDepartment}`);
    return params;
};

const buildUnpaginatedParams = (params) => [
    ...params.filter(p => !p.startsWith('limit=') && !p.startsWith('offset=')),
    'limit=0'
];

const toPayslipReportItem = (employee) => ({
    id: employee.payslip_id,
    State: employee.payslip_state,
    Employee: employee.name,
    email: employee.email
});

const EmployeeNav = ({
    setPayslipData, selectedMonth,
    setReport, setIsloading, setEmployeeId, setContractId, setFilteredEmployees,
    setIsMonthClosed, refreshTrigger
}) => {
    const { t } = useTranslation();
    const [employees, setEmployees] = useState([]);
    const [errorMessage, setErrorMessage] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [activeTab, setActiveTab] = useState(null);
    const [currentMonth, setCurrentMonth] = useState(selectedMonth || new Date().toISOString().slice(0, 7)); // Format: YYYY-MM
    const [sortOrder, setSortOrder] = useState('asc');
    const [categories, setCategories] = useState([]);
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [selectedDepartment, setSelectedDepartment] = useState('');
    const [showFilterPopover, setShowFilterPopover] = useState(false);
    const [offset, setOffset] = useState(0);
    const [limit] = useState(20);
    const [totalCount, setTotalCount] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const filterBtnRef = useRef(null);
    const popoverRef = useRef(null);
    const categoryOptions = [
        ...categories.map(cat => ({
            value: cat.id,
            label: cat.name
        }))
    ];
    const departmentOptions = departments.map(dept => ({
        value: dept.id,
        label: dept.name
    }));

    useEffect(() => {
        apiService.getElement('/employee/categories')
            .then(res => {
                setCategories(res.data.categories || []);
            })
            .catch(() => setCategories([]));
        apiService.getElement('/employee/departments')
            .then(res => {
                setDepartments(res.data.departments || []);
            })
            .catch(() => setDepartments([]));
    }, []);

    // Récupère en arrière-plan la liste complète (non paginée) pour les actions groupées
    const loadAllEmployeesForBulkActions = (params) => {
        apiService.getElement(`/month_overview?${buildUnpaginatedParams(params).join('&')}`).then(res => {
            setFilteredEmployees(res.data.employees || []);
        }).catch(e => console.error("Error fetching full list for bulk actions", e));
    };

    const showEmployees = (fetchedEmployees, pagination) => {
        setEmployees(fetchedEmployees);
        setTotalCount(pagination?.total_count || 0);
        setTotalPages(pagination?.total_pages || 1);
        // L'état des bulletins est directement dans l'objet 'employee',
        // il n'est pas nécessaire de le gérer dans un état séparé.
    };

    const showNoEmployee = () => {
        setEmployees([]);
        setFilteredEmployees([]);
        setTotalCount(0);
        setTotalPages(1);
    };

    const activateFirstEmployee = (firstEmployee) => {
        setIsloading(true);
        console.log('First employee:', firstEmployee);
        if (activeTab && activeTab === firstEmployee.id) {
            if (firstEmployee.payslip_id) {
                handleDownloadPDF(toPayslipReportItem(firstEmployee));
            } else {
                clearPayslipDisplay(firstEmployee.id);
            }
        } else {
            setActiveTab(firstEmployee.id);
            if (!firstEmployee.payslip_id) {
                setIsloading(false);
            }
        }
    };

    const fetchEmployees = async (month = currentMonth) => {
        try {
            const params = buildMonthOverviewParams({ offset, limit, month, sortOrder, searchQuery, selectedCategories, selectedDepartment });

            // Utiliser le point de terminaison unifié optimisé
            const response = await apiService.getElement(`/month_overview?${params.join('&')}`);
            const { employees: fetchedEmployees, pagination, is_month_closed } = response.data;

            if (setIsMonthClosed) {
                setIsMonthClosed(is_month_closed || false);
            }

            loadAllEmployeesForBulkActions(params);

            if (!fetchedEmployees || fetchedEmployees.length === 0) {
                showNoEmployee();
                return;
            }

            showEmployees(fetchedEmployees, pagination);

            // Activer le premier employé si aucun n'est sélectionné ou si le mois a changé
            if (!activeTab || month !== currentMonth) {
                activateFirstEmployee(fetchedEmployees[0]);
            }
        } catch (error) {
            console.error('There was an error fetching month overview!', error);
            setErrorMessage(t('employeePayslipNav.loadEmployeesError'));
        }
    };

    useEffect(() => {
        setCurrentMonth(selectedMonth);
        fetchEmployees(selectedMonth);
        // eslint-disable-next-line
    }, [searchQuery, selectedCategories, selectedDepartment, sortOrder, offset, limit, selectedMonth, refreshTrigger]);

    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
    };

    // Cette fonction n'a plus besoin de faire d'appel API pour trouver le bulletin
    const findPayslipForEmployee = async (employeeId) => {
        setIsloading(true);
        const employee = employees.find(emp => emp.id === employeeId);

        if (employee && employee.payslip_id) {
            // Utiliser l'ID du bulletin déjà récupéré pour télécharger le PDF
            handleDownloadPDF(toPayslipReportItem(employee));
        } else {
            // Aucun bulletin n'existe pour cet employé ce mois-ci
            clearPayslipDisplay(employeeId);
        }
    };

    const clearPayslipDisplay = (employeeId) => {
        setPayslipData(null);
        const employeeName = employees.find(emp => emp.id === employeeId)?.name || '';
        setReport({
            Employee: employeeName,
            Ref: t('employeePayslipNav.noPayslipAvailable'),
            De: '',
            Vers: ''
        });
        setIsloading(false);
    };

    const handleDownloadPDF = (item) => {
        apiService.getElement(`/payslip/json/${item.id}`)
            .then(response => {
                setPayslipData(response.data);
                setReport(item);
            })
            .catch(error => {
                console.error('Error downloading payslip JSON:', error);
                setErrorMessage(t('employeePayslipNav.downloadDataError'));
                clearPayslipDisplay(activeTab);
            })
    };

    const sidebarItems = employees.map(employee => ({
        id: employee.id,
        label: employee.name,
        icon: <FaUser className="w-5 h-5 text-gray-400" />,
        // Utiliser directement la propriété de l'objet employé
        payslipState: employee.payslip_state
    }));

    const handleTabChange = (employeeId) => {
        if (activeTab === employeeId) return;
        
        setPayslipData(null);
        setIsloading(true);
        setActiveTab(employeeId);
        setEmployeeId(employeeId);
        console.log('Active tab changed to:', employeeId);
        setReport({
            Employee: employees.find(emp => emp.id === employeeId)?.name || '',
            Ref: '',
            De: '',
            Vers: ''
        });

        const selectedEmployee = employees.find(emp => emp.id === employeeId);
        console.log('Selected employee:', selectedEmployee);
        if (selectedEmployee && selectedEmployee.contract_id) {
            setContractId(selectedEmployee.contract_id);
            console.log('Selected contract ID:', selectedEmployee.contract_id);
        }
        // L'appel à findPayslipForEmployee est maintenant géré par le useEffect sur activeTab
    };

    // Pagination controls
    const handlePrevPage = () => {
        if (offset - limit >= 0) setOffset(offset - limit);
    };
    const handleNextPage = () => {
        if (offset + limit < totalCount) setOffset(offset + limit);
    };

    useEffect(() => {
        function handleClickOutside(event) {
            if (
                popoverRef.current &&
                !popoverRef.current.contains(event.target) &&
                !filterBtnRef.current.contains(event.target)
            ) {
                setShowFilterPopover(false);
            }
        }
        if (showFilterPopover) {
            document.addEventListener("mousedown", handleClickOutside);
        } else {
            document.removeEventListener("mousedown", handleClickOutside);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [showFilterPopover]);

    useEffect(() => {
        if (activeTab) {
            setEmployeeId(activeTab);
            const selectedEmployee = employees.find(emp => emp.id === activeTab);
            if (selectedEmployee && selectedEmployee.contract_id) {
                setContractId(selectedEmployee.contract_id);
            }

            // La dépendance à currentMonth n'est plus nécessaire
            findPayslipForEmployee(activeTab);
        }
        // eslint-disable-next-line
    }, [activeTab]);

    return (
        <div className="flex flex-col h-[calc(100vh-140px)]">
            <div className="mb-2 bg-white rounded-lg p-4 pb-2 shadow-sm border border-gray-100 flex-shrink-0">

                <div className="flex items-center mb-2 gap-2 relative">
                    <input
                        type="text"
                        placeholder={t('employeePayslipNav.searchPlaceholder')}
                        value={searchQuery}
                        onChange={handleSearchChange}
                        className="border-gray-200 border py-2 px-4 w-full rounded-md focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                    />
                    <button
                        ref={filterBtnRef}
                        className="py-3 px-2 rounded hover:bg-gray-100 border border-gray-200"
                        onClick={() => setShowFilterPopover(v => !v)}
                        title={t('employeePayslipNav.filters')}
                    >
                        <FaFilter />
                    </button>
                    {/* Popover */}
                    {showFilterPopover && (
                        <div
                            ref={popoverRef}
                            className="absolute right-0 top-12 z-50 bg-white rounded-lg shadow-lg p-4 min-w-[220px] border"
                        >
                            <div className="mb-4">
                                <label className="block font-semibold mb-1">{t('employeePayslipNav.sortByName')}</label>
                                <div className="flex gap-3">
                                    <button
                                        className={`flex items-center gap-1 px-2 py-1 rounded ${sortOrder === 'asc' ? 'bg-orange-100 text-orange-600' : 'bg-gray-100'}`}
                                        onClick={() => {
                                            setSortOrder('asc');
                                            setShowFilterPopover(false);
                                        }}
                                    >
                                        <FaSortAlphaUp /> A-Z
                                    </button>
                                    <button
                                        className={`flex items-center gap-1 px-2 py-1 rounded ${sortOrder === 'desc' ? 'bg-orange-100 text-orange-600' : 'bg-gray-100'}`}
                                        onClick={() => {
                                            setSortOrder('desc');
                                            setShowFilterPopover(false);
                                        }}
                                    >
                                        <FaSortAlphaDown /> Z-A
                                    </button>
                                </div>
                            </div>
                            <div className="mb-4">
                                <label className="block font-semibold mb-1">{t('employeePayslipNav.label')}</label>
                                <Select
                                    isMulti
                                    options={categoryOptions}
                                    value={categoryOptions.filter(opt => selectedCategories.includes(String(opt.value)))}
                                    onChange={options => setSelectedCategories(options.map(opt => String(opt.value)))}
                                    className="w-full text-sm"
                                    classNamePrefix="react-select"
                                />
                            </div>
                            <div>
                                <label className="block font-semibold mb-1">{t('employeePayslipNav.departmentLabel')}</label>
                                <Select
                                    isClearable
                                    options={departmentOptions}
                                    value={departmentOptions.find(opt => String(opt.value) === selectedDepartment) || null}
                                    onChange={option => setSelectedDepartment(option ? String(option.value) : '')}
                                    placeholder={t('employeePayslipNav.allDepartments')}
                                    aria-label={t('employeePayslipNav.filterByDepartmentAria')}
                                    className="w-full text-sm"
                                    classNamePrefix="react-select"
                                />
                            </div>
                        </div>
                    )}
                </div>
                <div className="text-xs text-end text-gray-500 mt-1">
                    {t('employeePayslipNav.employeesFound', { count: employees.length })}
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                    {selectedCategories.length > 0 && selectedCategories.map(catId => {
                        const cat = categories.find(c => String(c.id) === String(catId));
                        return (
                            <span key={catId} className="bg-orange-100 text-orange-700 px-2 py-1 rounded text-xs flex items-center gap-1">
                                {cat?.name}
                                <button
                                    className="ml-1 text-orange-500 hover:text-orange-700"
                                    onClick={() => setSelectedCategories(selectedCategories.filter(id => id !== catId))}
                                    title={t('employeePayslipNav.removeLabel')}
                                >×</button>
                            </span>
                        );
                    })}
                    {selectedDepartment && (
                        <span className="bg-orange-100 text-orange-700 px-2 py-1 rounded text-xs flex items-center gap-1">
                            {departments.find(d => String(d.id) === String(selectedDepartment))?.name}
                            <button
                                className="ml-1 text-orange-500 hover:text-orange-700"
                                onClick={() => setSelectedDepartment('')}
                                title={t('employeePayslipNav.removeLabel')}
                            >×</button>
                        </span>
                    )}
                </div>
            </div>
            {employees.length > 0 ? (
                <div className="flex flex-col h-full overflow-hidden" style={{ minHeight: '400px' }}>
                    <div className="flex-1 overflow-y-auto min-h-0 mb-2 border rounded-lg border-gray-100 shadow-inner">
                        <SecondarySidebar
                            menuItems={sidebarItems}
                            setActiveTab={handleTabChange}
                            activeTab={activeTab}
                        />
                    </div>
                    {/* Pagination controls sticky */}
                    <div className="bg-white py-3 flex justify-center items-center border-t border-gray-100 flex-shrink-0">
                        <div className="join shadow-sm">
                            <button
                                className="join-item btn btn-sm hover:!bg-orange-100"
                                onClick={handlePrevPage}
                                disabled={offset === 0}
                                style={{ opacity: offset === 0 ? 0.5 : 1, cursor: offset === 0 ? 'not-allowed' : 'pointer' }}
                            >
                                «
                            </button>
                            <button className="join-item btn btn-sm cursor-default">
                                {t('employeePayslipNav.page', { current: Math.floor(offset / limit) + 1, total: totalPages })}
                            </button>
                            <button
                                className="join-item btn btn-sm hover:!bg-orange-100"
                                onClick={handleNextPage}
                                disabled={offset + limit >= totalCount}
                                style={{ opacity: offset + limit >= totalCount ? 0.5 : 1, cursor: offset + limit >= totalCount ? 'not-allowed' : 'pointer' }}
                            >
                                »
                            </button>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="flex flex-col items-center justify-center h-[calc(100vh-200px)] text-gray-500">
                    {t('employeePayslipNav.noActiveContractEmployee')}
                </div>
            )}
            {errorMessage && <div className="text-red-600 mt-4">{errorMessage}</div>}
        </div>
    );
};
export default EmployeeNav;